import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { auth } from '../firebase'; // Import the initialized auth instance
import { api } from '../services/api';
import { isFullyOnboardedBusiness } from '../utils/businessOnboarding';
import {
  storePendingGoogleCredential,
  storePendingGoogleOAuthCredential,
  clearAllPendingLinkCredentials,
  storePendingEmailCredential,
} from '../utils/authLinking';

const AuthContext = createContext();

async function loadBusinessForUser(user) {
  if (!user) return null;

  if (user.uid) {
    try {
      const byUid = await api.getBusiness(user.uid);
      if (byUid?.data && isFullyOnboardedBusiness(byUid.data)) {
        localStorage.setItem('currentBusiness', JSON.stringify(byUid.data));
        return byUid.data;
      }
    } catch (_) {
      // Fall through to email lookup.
    }
  }

  if (user.email) {
    const byEmail = await api.getBusinessByEmail(user.email);
    if (byEmail?.data && isFullyOnboardedBusiness(byEmail.data)) {
      localStorage.setItem('currentBusiness', JSON.stringify(byEmail.data));
      return byEmail.data;
    }
  }

  localStorage.removeItem('currentBusiness');
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessLoading, setBusinessLoading] = useState(true);

  console.log('AuthProvider initialized');

  useEffect(() => {
    console.log('AuthProvider useEffect called');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? user.email : 'No user');
      if (user) {
        setUser(user);
        try {
          setBusinessLoading(true);
          await loadBusinessForUser(user);
        } catch (error) {
          console.error('Error loading business:', error);
        } finally {
          setBusinessLoading(false);
        }
      } else {
        setUser(null);
        localStorage.removeItem('currentBusiness');
        setBusinessLoading(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const signInWithEmail = async (email, password) => {
    try {
      console.log('AuthContext: signInWithEmail called');
      const result = await signInWithEmailAndPassword(auth, email, password);
      storePendingEmailCredential(email, password);
      console.log('AuthContext: signInWithEmail successful', result.user.email);
      return result;
    } catch (error) {
      console.error('AuthContext: signInWithEmail error', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      console.log('AuthContext: signInWithGoogle called');
      const result = await signInWithPopup(auth, provider);
      storePendingGoogleCredential(result);
      console.log('AuthContext: signInWithGoogle successful', result.user.email);
      return result;
    } catch (error) {
      console.error('AuthContext: signInWithGoogle error', error);

      // Gmail already tied to another sign-in method (usually phone from mobile app).
      if (error.code === 'auth/account-exists-with-different-credential') {
        const pendingCred = error.customData?.credential;
        const email = error.customData?.email;
        if (pendingCred && email) {
          storePendingGoogleOAuthCredential(pendingCred);
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (methods.includes('phone')) {
            const linkErr = new Error(
              'This Gmail is linked to a phone account. Verify your phone number on the next screen.',
            );
            linkErr.code = 'auth/needs-phone-verification';
            throw linkErr;
          }
        }
      }

      throw error;
    }
  };

  const signUpWithEmail = async (email, password) => {
    try {
      console.log('AuthContext: signUpWithEmail called');
      const result = await createUserWithEmailAndPassword(auth, email, password);
      storePendingEmailCredential(email, password);
      console.log('AuthContext: signUpWithEmail successful', result.user.email);
      return result;
    } catch (error) {
      console.error('AuthContext: signUpWithEmail error', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      clearAllPendingLinkCredentials();
      await firebaseSignOut(auth);
      setUser(null);
      localStorage.removeItem('currentBusiness'); // Clear business data on logout
      // No navigation here - let components handle navigation
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  /**
   * Force-reload the Firebase user object so phoneNumber and providerData
   * update immediately after OTP linking (added with Superowl phone gate).
   */
  const refreshUser = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    setUser({ ...auth.currentUser });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: loading || businessLoading,
        signInWithEmail,
        signInWithGoogle,
        signUpWithEmail,
        signOut,
        refreshUser,
      }}
    >
      {!loading && !businessLoading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
